import { Controller } from '@hotwired/stimulus';
import modules from '../datatables/registry';

// Monta a DataTable quando o HTML aparece (pagina ou modal injetado) e destroi
// quando ele sai. Substitui o `javascript_pack_tag` no corpo da view: o jQuery
// reavaliava o pack a cada injecao do modal e a tabela anterior ficava orfa,
// redesenhando fora da tela, mandando POST sem token CSRF e lendo filtros que
// nao existem mais (Sentry 2N9/2N7/2P5/2PK/2NH/2NJ).
//
// Vai num bloco EM VOLTA do filtro e da <table>, nunca na tabela: o DataTables
// tira a tabela do lugar para embrulhar, o Stimulus entenderia como
// disconnect + connect e o init entraria em loop. Os eventos (FILTRAR,
// Ctrl+Enter, contador da chave) sao `data-action` deste bloco, em vez de
// `.on`/`.off` em seletor de documento: so o FILTRAR de dentro do bloco filtra
// esta tabela, e nada fica ligado depois que o HTML sai.
//
//   div data-controller="datatable" data-datatable-module-value="cte_xmls"
//       data-action="click->datatable#filter keydown.ctrl+enter->datatable#filter"
//     = render "cte_xmls/filter"
//     table ...
//
// Connects to data-controller="datatable"

export default class extends Controller {
  static get targets() {
    return ['counter'];
  }

  static get values() {
    return { module: String };
  }

  async connect() {
    try {
      const { load } = await modules[this.moduleValue]();
      if (!this.element.isConnected || this.table) return;

      this.table = load(this.element);
      this.prepareFilter();
      this.adjustWhenShown();
    } catch (error) {
      this.destroyPartial();
      // O modulo vem por `import()` do runtime da pagina. Se houve deploy desde
      // que ela abriu, o chunk antigo nao existe mais no servidor (404) e so um
      // F5 resolve: avisa em vez de deixar o modal sem tabela.
      if (error && error.name === 'ChunkLoadError') {
        this.element.insertAdjacentHTML(
          'afterbegin',
          '<div class="alert alert-warning py-2">O sistema foi atualizado. ' +
            'Recarregue a página (F5) para carregar esta tabela.</div>'
        );
      }
      throw error;
    }
  }

  disconnect() {
    this.observer?.disconnect();
    this.table?.destroy();
    this.table = null;
  }

  // `#filtering` dentro da tabela e o que custom_datatable.js le para deixar o
  // draw passar e mandar `filtering=true` ao servidor (era o `loadDtFilters`).
  // O FILTRAR nasce desabilitado quando a pagina tem botao no DOM ready; daqui
  // em diante a tabela existe e o clique tem destino.
  prepareFilter() {
    this.filtering = document.createElement('input');
    this.filtering.type = 'hidden';
    this.filtering.id = 'filtering';
    this.table.table().node().appendChild(this.filtering);
    this.element.querySelectorAll('.dt-filter-action').forEach((button) => {
      button.disabled = false; // eslint-disable-line no-param-reassign
    });
  }

  // FILTRAR (clique em `.dt-filter-action` do bloco) e Ctrl+Enter num campo do
  // filtro. `draw()` e nao `draw('page')`: filtrar de novo volta para a pagina 1.
  filter(event) {
    if (event.type === 'click' && !event.target.closest('.dt-filter-action')) {
      return;
    }
    if (!this.table) return;
    // Ctrl+Enter nao passa pelo `disabled` do botao, que fica travado enquanto
    // o request esta em voo (custom_datatable.js).
    if (this.element.querySelector('.dt-filter-action')?.disabled) return;

    event.preventDefault();
    // Nao chega ao `keydown` de documento que o `loadDtFilters` da tela de tras
    // liga: sem isso o Ctrl+Enter no modal refiltrava a tabela do host tambem.
    event.stopPropagation();
    this.filtering.value = 'true';
    this.table.draw();
  }

  // Contador "x/44" da chave do documento.
  count({ target }) {
    this.counterTargets.forEach((counter) => {
      counter.textContent = target.value.length; // eslint-disable-line no-param-reassign
    });
  }

  // Se o `load()` lancou no meio da construcao, `this.table` nunca recebeu a
  // instancia e o `disconnect` nao a destruiria: sobravam os settings e um
  // `resize.DT-*` na window por tentativa, rodando em tabela desanexada.
  destroyPartial() {
    $(this.element)
      .find('table')
      .each((_i, table) => {
        if ($.fn.dataTable && $.fn.dataTable.isDataTable(table)) {
          $(table).DataTable().destroy();
        }
      });
    this.table = null;
  }

  // Tabela em aba escondida nasce com largura 0 e o cabecalho do scrollX fica
  // desalinhado. Antes era um `.nav-link` global com `draw()`: qualquer clique
  // de menu reconsultava o servidor, inclusive de tabelas orfas. Aqui so
  // realinha, sem consulta, quando o bloco passa de escondido para visivel.
  // Redimensionar a janela fica com o `resize.DT` do proprio DataTables.
  adjustWhenShown() {
    let visible = this.element.offsetWidth > 0;
    this.observer = new ResizeObserver(() => {
      const now = this.element.offsetWidth > 0;
      if (now && !visible) this.table?.columns.adjust();
      visible = now;
    });
    this.observer.observe(this.element);
  }
}
